import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { serverHost, activeSessionClaims } from "../http_common.js";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { CEOThemeProvider } from "./CEOThemeProvider";
import CEOThemeSwitcher from "./CEOThemeSwitcher";
import { startCEOAction } from "./ceoReducer";
import {
  CEO_WORKSPACE_PATH,
  hasCEORole,
  isCEOAuthenticated,
  selectCEOSession,
} from "./ceoAuth";
import "./ceoLogin.css";

const defaultCredentials = ({ userName, password }) => ({
  username: userName,
  password,
});
const REMEMBER_KEY = "universe_ceo_remembered_id";
function savedID() {
  try {
    return localStorage.getItem(REMEMBER_KEY) || "";
  } catch {
    return "";
  }
}
function rememberID(value) {
  try {
    if (value) localStorage.setItem(REMEMBER_KEY, value);
    else localStorage.removeItem(REMEMBER_KEY);
  } catch {
    /* Optional device preference. */
  }
}
function defaultEndpoint() {
  const base = (process.env.REACT_APP_SERVER_SCRIPT_HOST || serverHost).replace(/\/+$/, "");
  return process.env.REACT_APP_CEO_LOGIN_ENDPOINT || `${base}/ceo/staff/login`;
}

function LoginForm({
  loginEndpoint = defaultEndpoint(),
  buildCredentials = defaultCredentials,
  withCredentials = false,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const session = useSelector(selectCEOSession);
  const controller = useRef(null);
  const [userName, setUserName] = useState(savedID);
  const [remember, setRemember] = useState(() => Boolean(savedID()));
  const [capsLock, setCapsLock] = useState(false);
  const [password, setPassword] = useState("");
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => () => controller.current?.abort(), []);

  if (isCEOAuthenticated(session))
    return <Navigate to={CEO_WORKSPACE_PATH} replace />;

  async function submit(event) {
    event.preventDefault();
    if (busy) return;
    setError("");
    if (!userName.trim() || !password) {
      setError("Enter your CEO ID or email and password.");
      return;
    }
    if (!loginEndpoint) {
      setError("CEO sign-in is not configured yet.");
      return;
    }
    setBusy(true);
    controller.current = new AbortController();
    try {
      const response = await axios.post(
        loginEndpoint,
        buildCredentials({ userName: userName.trim(), password }),
        {
          timeout: 30000,
          signal: controller.current.signal,
          withCredentials,
        },
      );
      const loginData = response.data;
      const sessionPayload = {
        staffPayload: { staffData: loginData?.staffData },
        authenticationType: loginData?.authenticationType,
        sessionToken: loginData?.sessionToken || null,
      };
      const tokenClaims = activeSessionClaims(sessionPayload.sessionToken, ["STAFF_ACCESS"]);
      if (loginData?.status !== 200 || !hasCEORole(sessionPayload) ||
          tokenClaims?.role !== "CEO" || tokenClaims?.source !== "CEO") {
        setError(
          loginData?.message || "This account does not have CEO access.",
        );
        return;
      }
      rememberID(remember ? userName.trim() : "");
      dispatch(startCEOAction(sessionPayload));
      setPassword("");
      navigate(CEO_WORKSPACE_PATH, { replace: true });
    } catch (failure) {
      if (axios.isCancel(failure)) return;
      setError(
        failure.response?.data?.message ||
          (failure.code === "ECONNABORTED"
            ? "Sign-in timed out. Please try again."
            : "Unable to sign in. Check your details and try again."),
      );
    } finally {
      if (!controller.current?.signal.aborted) setBusy(false);
    }
  }

  return (
    <main className="ceo-login">
      <section className="ceo-login__intro">
        <div className="ceo-login__brand">
          <span>U</span>
          <strong>Universe</strong>
        </div>
        <div>
          <span className="ceo-login__eyebrow">EXECUTIVE WORKSPACE</span>
          <h1>
            Lead with
            <br />a clear view.
          </h1>
          <p>
            Company performance, leadership and decisions. One executive
            workspace.
          </p>
        </div>
        <div className="ceo-login__scope">
          <span>Company oversight</span>
          <span>Territory leadership</span>
          <span>Executive decisions</span>
        </div>
      </section>
      <section className="ceo-login__form-area">
        <div className="ceo-login__form">
          <span className="ceo-login__eyebrow">CEO ACCESS</span>
          <h2>Welcome back</h2>
          <p className="ceo-subtle">Sign in to your executive account.</p>
          <form onSubmit={submit} aria-busy={busy}>
            <label htmlFor="ceo-login-email">CEO ID or email</label>
            <input
              id="ceo-login-email"
              className="ceo-input"
              type="text"
              name="user_name"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              value={userName}
              onChange={(event) => setUserName(event.target.value)}
              required
              disabled={busy}
            />
            <label htmlFor="ceo-login-password">Password</label>
            <div className="ceo-login__password">
              <input
                id="ceo-login-password"
                className="ceo-input"
                type={visible ? "text" : "password"}
                name="stud_password"
                autoComplete="current-password"
                onKeyUp={(event) =>
                  setCapsLock(Boolean(event.getModifierState?.("CapsLock")))
                }
                onBlur={() => setCapsLock(false)}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                disabled={busy}
              />
              <button
                type="button"
                aria-label={visible ? "Hide password" : "Show password"}
                aria-pressed={visible}
                onClick={() => setVisible(!visible)}
              >
                {visible ? (
                  <VisibilityOffOutlinedIcon />
                ) : (
                  <VisibilityOutlinedIcon />
                )}
              </button>
            </div>
            {capsLock && (
              <p className="ceo-subtle" role="status">
                Caps Lock is on.
              </p>
            )}
            <label className="ceo-login__remember">
              <input
                type="checkbox"
                checked={remember}
                onChange={(event) => setRemember(event.target.checked)}
                disabled={busy}
              />{" "}
              Remember my CEO ID on this device
            </label>
            {error && (
              <div className="ceo-login__error" role="alert">
                {error}
              </div>
            )}
            <button
              className="ceo-button ceo-button--primary ceo-login__submit"
              type="submit"
              disabled={busy}
            >
              {busy ? "Signing in…" : "Enter CEO workspace"}
              <ArrowForwardRoundedIcon />
            </button>
          </form>
          <details className="ceo-login__appearance">
            <summary>Appearance</summary>
            <CEOThemeSwitcher />
          </details>
        </div>
      </section>
    </main>
  );
}

export default function CEOLogin(props) {
  return (
    <CEOThemeProvider>
      <LoginForm {...props} />
    </CEOThemeProvider>
  );
}
